/**
 * One campaign, everything this console reads about it, on one card — and since
 * 2026-10-21 (A7, M2) the place it is managed from: the campaign as X holds it with its
 * four figures over seven days, every line item under it with the same four, each line
 * item's targeting criteria with the country named where the list in hand names it, and
 * each promoted tweet with its text; Edit on the campaign and on each line item, Remove
 * on each place, Add ad under a PAUSED line item, Delete on each line item and on the
 * campaign.
 *
 * 🔴 **Writes only through the named handlers, and each one re-reads.** The five writes
 * this file makes are `saveCampaign` (`api.updateCampaign`), `removeCampaign`
 * (`api.deleteCampaign`), `saveLineItem` (`api.updateLineItem`), `removeLineItem`
 * (`api.deleteLineItem`) and `removePlace` (`api.removeTargetingCriterion`); the attach
 * is `AddAd`'s, through `lib/ad-run.ts`. Start, Pause and Promote stay on the line item's
 * own screen in the Line items panel, and the card points there. After every write the object is read again and what
 * X holds is shown — never what was typed. `campaign-card-copy.test.ts` holds the list of
 * handlers against this file: a write under any other name reddens it.
 *
 * The decisions are in `lib/campaign-card.ts` (paging to the end, the country query, the
 * tweet-id ceiling, the name lookup), `lib/campaign-manage.ts` (only what changed is
 * sent; the answers' shapes) and `lib/stats.ts` (the window, the five states of a figure,
 * X's twenty-id ceiling); the words in `lib/campaign-card-copy.ts`,
 * `lib/campaign-manage-copy.ts` and the copy modules they borrow from; this file renders
 * them and decides nothing.
 *
 * 🔴 **The figures are the Spend column's path, nothing of this card's own**: the same
 * `GET /stats`, the same seven-day window of whole hours built once per card, the same
 * `metricRowState` for the five states, the same words — and the figures are cleared
 * before every read, for the reason the Campaigns screen states (stats-screen S0).
 *
 * 🔴 **Every read is checked against the campaign on screen after it answers**, as the
 * Line items section does: the card is one component re-pointed at another row, and
 * a page that lands for the previous campaign would otherwise sit under this one's
 * name.
 */
import { useEffect, useRef, useState } from "react";
import {
	type Campaign,
	type LineItem,
	type PromotedTweet,
	type StatsAnswer,
	type TargetingCriterion,
	type TargetingLocation,
	type Tweet,
	UnauthorizedError,
	type XAccount,
	api,
} from "../lib/api.ts";
import {
	CARD_CAMPAIGN_PANEL,
	CARD_INTRO,
	CARD_LINE_ITEMS_PANEL,
	CARD_LINE_ITEM_EMPTY,
	CARD_LINE_ITEM_PANEL,
	COUNTRIES_NOT_READ,
	FIGURE_LABELS,
	HIDE_LINE_ITEM,
	METRICS,
	NO_AD,
	OPEN_LINE_ITEMS,
	SHOW_LINE_ITEM,
	TWEETS_NOT_READ,
	countryNote,
	figureCellText,
	flightLocal,
	noLineItemsText,
	objectiveLabel,
	originText,
	shownLabel,
	tweetAside,
	tweetTextCell,
	whenText,
} from "../lib/campaign-card-copy.ts";
import {
	COUNTRIES_PAGE_SIZE,
	type TweetAsk,
	allPages,
	countryName,
	tweetIdsToAsk,
	tweetText,
	tweetsById,
} from "../lib/campaign-card.ts";
import { LINE_ITEMS_HERE, servableText } from "../lib/campaign-copy.ts";
import {
	ADD_AD,
	BUDGET_HINT,
	CAMPAIGN_DAILY_ON_AD_GROUPS,
	CAMPAIGN_EDIT_HELP,
	CAMPAIGN_NAME_HELP,
	CAMPAIGN_READ_ONLY,
	CANCEL,
	CONFIRM_DELETE,
	DELETE_CAMPAIGN,
	DELETE_LINE_ITEM,
	EDIT,
	FLIGHT_CAUTION,
	INSTRUMENT_FIXED,
	KEEP,
	LINE_ITEM_DAILY_HELP,
	LINE_ITEM_DAILY_READ_ONLY,
	LINE_ITEM_EDIT_HELP,
	LINE_ITEM_NAME_HELP,
	LINE_ITEM_TOTAL_CAUTION,
	PAUSE_FIRST,
	REMOVE_PLACE,
	SAVE,
	SHAPE_FIXED,
	WRITES_HERE,
	campaignEditProblemText,
	deleteCampaignConfirm,
	deleteLineItemConfirm,
	deletedText,
	lineItemEditProblemText,
	removePlaceConfirm,
	savedText,
} from "../lib/campaign-manage-copy.ts";
import {
	type CampaignEdit,
	type LineItemEdit,
	campaignDailyOffered,
	campaignEdit,
	campaignUpdate,
	dailyEditable,
	deletable,
	deleteOutcome,
	lineItemEdit,
	lineItemUpdate,
	openingEdit,
	updateOutcome,
} from "../lib/campaign-manage.ts";
import { adsManagerText, budgetSummary } from "../lib/create-ad-copy.ts";
import { approvalNote, promotedLabel } from "../lib/creative-copy.ts";
import { bidText, creativeHint, lineItemBudgetText } from "../lib/line-item-copy.ts";
import { startGate } from "../lib/line-items.ts";
import { offersRawAnswer } from "../lib/raw-answer.ts";
import {
	FIGURES_NOTE,
	SPEND_NOTES,
	SPEND_READ_FAILED,
	coveredText,
	figuresReadText,
} from "../lib/stats-copy.ts";
import { idsForStats, metricRowState, sevenDayWindow } from "../lib/stats.ts";
import { NO_CRITERIA, criterionLabel, loadedLabel } from "../lib/targeting-copy.ts";
import { Badge, Button, InfoBanner, Input, Panel, Table } from "../ui/index.ts";
import { AddAd } from "./AddAd.tsx";
import { RawAnswer } from "./RawAnswer.tsx";

interface Props {
	campaign: Campaign;
	account: XAccount;
	/** `canWrite` on the account, as the campaigns screen resolved it: every write here is behind it. */
	writable: boolean;
	/**
	 * The code to label a spend figure with, as the campaigns screen resolved it from
	 * the account's funding instruments. 🔴 A prop and not a second read, for the reason
	 * the Line items section gives: X's analytics answer carries no currency, and `null`
	 * is a real answer that prints the raw micro figure rather than a guessed code.
	 */
	currency: string | null;
	/**
	 * The Line items panel, where each line item's own screen — Start, Pause, Promote — opens:
	 * the parent opens it for this campaign.
	 */
	onOpenLineItems: () => void;
	/**
	 * Told after a save or a delete under the campaign, so the list's row is read again.
	 * 🔴 After a line item's delete it says so: the Line items panel beside the card still
	 * lists that line item, and its Open would lead to a screen that writes to X.
	 */
	onChanged: (after?: "line-item-delete") => void;
	/** Told once X confirmed the campaign's delete: the parent closes the card and reads the list again. */
	onDeleted: () => void;
	/**
	 * Opened by the row's Edit: the card mounts in its edit form (`openingEdit`). Read once,
	 * at mount — the parent keys each opening, so a second Edit is a second card.
	 */
	startEditing?: boolean;
}

/** One read's state, the shape `useLoad` gives, held per line item here. */
type Read<T> =
	| { state: "loading" }
	| { state: "ready"; data: T }
	| { state: "failed"; why: string };

interface LineItemReads {
	criteria: Read<TargetingCriterion[]>;
	promoted: Read<PromotedTweet[]>;
}

/** What is said after a write, under the object it was about. */
interface Notice {
	variant: "success" | "warning" | "danger";
	title: string;
	body: string | null;
}

const NOT_ASKED: TweetAsk = { ids: [], notAsked: [], unnamed: 0 };

function why(e: unknown): string {
	return e instanceof Error ? e.message : String(e);
}

export function CampaignCard({
	campaign: given,
	account,
	writable,
	currency,
	onOpenLineItems,
	onChanged,
	onDeleted,
	startEditing = false,
}: Props) {
	/*
	 * 🔴 The campaign on the card is what X reported last, not the row the parent handed
	 * over: after a save the card reads it by id and shows that. The parent keys the card
	 * on the campaign's id, so another row is another card, never this one re-pointed.
	 */
	const [campaign, setCampaign] = useState<Campaign>(given);
	const [lineItems, setLineItems] = useState<Read<{ rows: LineItem[]; moreNotRead: boolean }>>({
		state: "loading",
	});
	const [reads, setReads] = useState<Record<string, LineItemReads>>({});
	const [countries, setCountries] = useState<
		Read<{ list: TargetingLocation[]; truncated: boolean }>
	>({ state: "loading" });
	const [ask, setAsk] = useState<TweetAsk>(NOT_ASKED);
	const [tweets, setTweets] = useState<Read<Map<string, Tweet>>>({ state: "loading" });
	/*
	 * The seven-day window, built once per card — see the campaigns screen: a window
	 * rebuilt on every render would change mid-hour and make two figures on one card
	 * answer two different questions.
	 */
	const [billedWindow] = useState(() => sevenDayWindow(new Date()));
	const [campaignFigures, setCampaignFigures] = useState<StatsAnswer | null>(null);
	const [campaignFiguresFailed, setCampaignFiguresFailed] = useState<string | null>(null);
	const [lineItemFigures, setLineItemFigures] = useState<StatsAnswer | null>(null);
	const [lineItemFiguresFailed, setLineItemFiguresFailed] = useState<string | null>(null);
	/** Which campaign the reads on screen are about, checked after every await. */
	const chosen = useRef(given.id);
	/*
	 * The card renders under the campaign table, so a click on a row's Card or Edit could
	 * open it below the fold and look like nothing happened (spec 2026-10-23). It brings
	 * itself into view once, when it mounts; each opening is a new mount.
	 */
	const self = useRef<HTMLElement>(null);
	useEffect(() => {
		self.current?.scrollIntoView({ block: "start" });
	}, []);

	// ── Managing: what is open, and what was said ───────────────────
	const [busy, setBusy] = useState(false);
	const [edit, setEdit] = useState<CampaignEdit | null>(() =>
		openingEdit(given, startEditing, writable),
	);
	const [editProblem, setEditProblem] = useState<string | null>(null);
	const [campaignNotice, setCampaignNotice] = useState<Notice | null>(null);
	const [confirmingCampaign, setConfirmingCampaign] = useState(false);
	const [editing, setEditing] = useState<{ id: string; edit: LineItemEdit } | null>(null);
	const [editingProblem, setEditingProblem] = useState<string | null>(null);
	const [confirming, setConfirming] = useState<string | null>(null);
	const [removing, setRemoving] = useState<{ lineItemId: string; id: string } | null>(null);
	const [addingAdTo, setAddingAdTo] = useState<string | null>(null);
	/**
	 * The line item whose edit, targeting and ads the third panel shows, by id — one at a
	 * time, as the Line items panel holds one: the row's Show sets it. Held as an id, so the
	 * re-read after every write keeps it pointed at the same line item, and a line item X
	 * no longer lists shows nothing rather than another's blocks.
	 */
	const [shown, setShown] = useState<string | null>(null);
	const [lineItemNotices, setLineItemNotices] = useState<Record<string, Notice>>({});
	const noteLineItem = (id: string, notice: Notice) =>
		setLineItemNotices((n) => ({ ...n, [id]: notice }));

	/**
	 * Everything under the campaign, read again: the line items to the end, each one's
	 * criteria and promoted tweets, and the tweets' text. Out here rather than only
	 * inside the effect because every write below ends in this read — what the card
	 * shows after a save or a delete is what X holds, read again.
	 */
	const readAll = () => {
		chosen.current = given.id;
		const asked = given.id;
		const current = () => chosen.current === asked;
		setLineItems({ state: "loading" });
		setReads({});
		setAsk(NOT_ASKED);
		setTweets({ state: "loading" });

		allPages<LineItem>(async (cursor) => {
			const page = await api.lineItems(account.xAccountId, asked, cursor);
			return { rows: page.lineItems, nextCursor: page.page.nextCursor };
		})
			.then(async (all) => {
				if (!current()) return;
				setLineItems({ state: "ready", data: { rows: all.rows, moreNotRead: all.moreNotRead } });
				for (const item of all.rows)
					setReads((r) => ({
						...r,
						[item.id]: { criteria: { state: "loading" }, promoted: { state: "loading" } },
					}));
				// Criteria and promoted tweets for every line item, each read on its own so
				// one failure is one line item's failure and not the card's.
				const promotedAll = await Promise.all(
					all.rows.map(async (item) => {
						const criteria = api
							.targetingCriteria(account.xAccountId, item.id)
							.then((page) => page.criteria);
						const promoted = api
							.promotedTweets(account.xAccountId, item.id)
							.then((page) => page.promotedTweets);
						const [c, p] = await Promise.allSettled([criteria, promoted]);
						if (!current()) return [];
						setReads((r) => ({
							...r,
							[item.id]: {
								criteria:
									c.status === "fulfilled"
										? { state: "ready", data: c.value }
										: { state: "failed", why: why(c.reason) },
								promoted:
									p.status === "fulfilled"
										? { state: "ready", data: p.value }
										: { state: "failed", why: why(p.reason) },
							},
						}));
						return p.status === "fulfilled" ? p.value : [];
					}),
				);
				if (!current()) return;
				// 🔴 One request for every tweet on the card, by id, PUBLISHED: the ceiling and
				// the deduplication are the module's.
				const toAsk = tweetIdsToAsk(promotedAll.flat());
				setAsk(toAsk);
				if (toAsk.ids.length === 0) {
					setTweets({ state: "ready", data: new Map() });
					return;
				}
				try {
					const page = await api.tweetsById(account.xAccountId, "PUBLISHED", toAsk.ids);
					if (!current()) return;
					setTweets({ state: "ready", data: tweetsById(page.tweets) });
				} catch (e) {
					if (!current() || e instanceof UnauthorizedError) return;
					setTweets({ state: "failed", why: why(e) });
				}
			})
			.catch((e) => {
				if (!current() || e instanceof UnauthorizedError) return;
				setLineItems({ state: "failed", why: why(e) });
			});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: the two ids ARE the subject; `readAll` closes over exactly those two
	useEffect(() => {
		readAll();
	}, [given.id, account.xAccountId]);

	/** The campaign by id, again: the read that does not lag, and the card's answer after a save. */
	const readCampaign = async () => {
		const { status, body } = await api.campaign(given.id, account.xAccountId);
		if (chosen.current !== given.id) return;
		if (status === 200 && typeof body === "object" && body !== null) {
			setCampaign(body as Campaign);
		}
	};

	// 🔴 The country list is asked once per card, with no `q`, at the page size the
	// whole list fitted on 2026-10-21; `truncated` is kept, because a name that is not
	// in a truncated list is a different fact from a name that is not in X's list.
	useEffect(() => {
		let live = true;
		setCountries({ state: "loading" });
		api
			.targetingLocations("", "COUNTRIES", COUNTRIES_PAGE_SIZE)
			.then((page) => {
				if (live)
					setCountries({
						state: "ready",
						data: { list: page.locations, truncated: page.truncated },
					});
			})
			.catch((e) => {
				if (!live || e instanceof UnauthorizedError) return;
				setCountries({ state: "failed", why: why(e) });
			});
		return () => {
			live = false;
		};
	}, []);

	/*
	 * 🔴 The campaign's four figures, cleared before the read for the reason the
	 * campaigns screen states: a figure left standing would sit against a row it was
	 * never about. One id, one request.
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: the campaign and the account ARE the subject; the window is built once on purpose
	useEffect(() => {
		let live = true;
		setCampaignFigures(null);
		setCampaignFiguresFailed(null);
		api
			.stats(account.xAccountId, "CAMPAIGN", [campaign.id], billedWindow)
			.then((answer) => {
				if (live) setCampaignFigures(answer);
			})
			.catch((e: unknown) => {
				if (!live || e instanceof UnauthorizedError) return;
				setCampaignFiguresFailed(why(e));
			});
		return () => {
			live = false;
		};
	}, [given.id, account.xAccountId]);

	const rows = lineItems.state === "ready" ? lineItems.data.rows : [];
	/*
	 * 🔴 The line items' figures: at most twenty ids, in the order the rows stand, and
	 * `cut` said under the table — the Campaigns screen's own arrangement. Cleared
	 * before every read.
	 */
	const picked = idsForStats(rows.map((r) => r.id));
	const askedFor = picked.ids.join(",");
	// biome-ignore lint/correctness/useExhaustiveDependencies: the account and the ids on screen ARE the subject; the window is built once on purpose
	useEffect(() => {
		let live = true;
		setLineItemFigures(null);
		setLineItemFiguresFailed(null);
		if (askedFor === "") return;
		api
			.stats(account.xAccountId, "LINE_ITEM", askedFor.split(","), billedWindow)
			.then((answer) => {
				if (live) setLineItemFigures(answer);
			})
			.catch((e: unknown) => {
				if (!live || e instanceof UnauthorizedError) return;
				setLineItemFiguresFailed(why(e));
			});
		return () => {
			live = false;
		};
	}, [account.xAccountId, askedFor]);

	// ── The five writes, each named, each ending in a read ──────────

	/** PUT the campaign's changed fields, then read it by id and show what X holds. */
	async function saveCampaign() {
		if (!writable || busy || edit === null) return;
		const body = campaignUpdate(account.xAccountId, campaign, edit);
		if (!body.ok) {
			setEditProblem(campaignEditProblemText(body.problem));
			return;
		}
		setEditProblem(null);
		setBusy(true);
		try {
			const { status, body: answered } = await api.updateCampaign(campaign.id, body.value);
			const said = savedText(updateOutcome(status, answered));
			setCampaignNotice({ variant: said.variant, title: said.text, body: null });
			if (status === 200) setEdit(null);
			await readCampaign();
			onChanged();
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			// The request may have reached X: the campaign is read again rather than assumed unchanged.
			setCampaignNotice({ variant: "danger", title: "The save got no answer.", body: why(e) });
			await readCampaign().catch(() => undefined);
		} finally {
			setBusy(false);
		}
	}

	/** DELETE the campaign after the in-page confirmation; X's answer is read, and the parent told. */
	async function removeCampaign() {
		if (!writable || busy || !confirmingCampaign) return;
		setConfirmingCampaign(false);
		setBusy(true);
		try {
			const { status, body } = await api.deleteCampaign(campaign.id, account.xAccountId);
			const outcome = deleteOutcome(status, body, "lineItems");
			setCampaignNotice(deletedText(outcome, "campaign", campaign.id));
			if (outcome.kind === "deleted") onDeleted();
			else {
				await readCampaign().catch(() => undefined);
				onChanged();
			}
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			setCampaignNotice({
				variant: "warning",
				title: "The delete got no answer — look before asking again.",
				body: why(e),
			});
			await readCampaign().catch(() => undefined);
			onChanged();
		} finally {
			setBusy(false);
		}
	}

	/** PUT a line item's changed fields, then read everything under the campaign again. */
	async function saveLineItem(item: LineItem) {
		if (!writable || busy || editing === null || editing.id !== item.id) return;
		const body = lineItemUpdate(account.xAccountId, item, editing.edit);
		if (!body.ok) {
			setEditingProblem(lineItemEditProblemText(body.problem));
			return;
		}
		setEditingProblem(null);
		setBusy(true);
		try {
			const { status, body: answered } = await api.updateLineItem(item.id, body.value);
			const said = savedText(updateOutcome(status, answered));
			noteLineItem(item.id, { variant: said.variant, title: said.text, body: null });
			if (status === 200) setEditing(null);
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			noteLineItem(item.id, { variant: "danger", title: "The save got no answer.", body: why(e) });
		} finally {
			setBusy(false);
			readAll();
			onChanged();
		}
	}

	/** DELETE a line item after the in-page confirmation; X's listing afterwards is shown as a reading. */
	async function removeLineItem(item: LineItem) {
		if (!writable || busy || confirming !== item.id) return;
		setConfirming(null);
		setBusy(true);
		try {
			const { status, body } = await api.deleteLineItem(item.id, account.xAccountId);
			noteLineItem(
				item.id,
				deletedText(deleteOutcome(status, body, "promotedTweets"), "line item", item.id),
			);
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			noteLineItem(item.id, {
				variant: "warning",
				title: "The delete got no answer — look before asking again.",
				body: why(e),
			});
		} finally {
			setBusy(false);
			readAll();
			// Asked, answered or not: a delete with no answer may have gone through too.
			onChanged("line-item-delete");
		}
	}

	/** DELETE one criterion after the in-page confirmation, then read the line item's criteria again. */
	async function removePlace(item: LineItem, criterion: TargetingCriterion) {
		if (!writable || busy || removing?.id !== criterion.id) return;
		setRemoving(null);
		setBusy(true);
		try {
			const { status, body } = await api.removeTargetingCriterion(criterion.id, account.xAccountId);
			noteLineItem(
				item.id,
				deletedText(deleteOutcome(status, body, null), "criterion", criterion.id),
			);
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			noteLineItem(item.id, {
				variant: "warning",
				title: "The removal got no answer — look before asking again.",
				body: why(e),
			});
		} finally {
			setBusy(false);
			readAll();
		}
	}

	const created = whenText(campaign.createdAt);
	const updated = whenText(campaign.updatedAt);
	const adsManager = adsManagerText(campaign.id);
	const list = countries.state === "ready" ? countries.data.list : null;
	const truncated = countries.state === "ready" && countries.data.truncated;
	const answer = tweets.state === "ready" ? tweets.data : null;
	const figuresRead = lineItemFigures ?? campaignFigures;
	const shownItem = rows.find((r) => r.id === shown) ?? null;

	const nameCell = (c: TargetingCriterion) => {
		const name = countryName(c, list);
		if (name !== null) return <span className="text-xs text-content">{name}</span>;
		const kind =
			c.targetingType !== "LOCATION" || c.locationType !== "COUNTRIES"
				? "other-level"
				: list === null
					? "list-not-read"
					: truncated
						? "truncated"
						: "not-in-list";
		return <span className="text-xs text-muted">{countryNote(kind)}</span>;
	};

	return (
		<section ref={self} className="@container grid gap-3 @3xl:grid-cols-2">
			{/*
			 * The campaign's panel: what X holds, its four figures, Edit and Delete. Each write
			 * and its guard is where it was; only the box around them changed.
			 */}
			<Panel
				className="@3xl:col-span-2"
				title={CARD_CAMPAIGN_PANEL}
				aside={
					<>
						<span className="text-sm font-medium text-content">{campaign.name}</span>
						<span className="font-mono text-xs text-muted">{campaign.id}</span>
					</>
				}
			>
				<p className="mt-1 text-sm text-muted">{CARD_INTRO}</p>
				<p className="mt-1 text-sm text-muted">{WRITES_HERE}</p>

				<dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
					<dt className="text-muted">Ads Manager</dt>
					<dd className="text-content">{adsManager ?? "no number: the id is not base36"}</dd>
					<dt className="text-muted">Status</dt>
					<dd>
						<Badge variant={campaign.entityStatus === "ACTIVE" ? "info" : "default"}>
							{campaign.entityStatus}
						</Badge>
						<span className="ml-2 text-content">
							{servableText(campaign.servable, campaign.reasonsNotServable)}
						</span>
					</dd>
					<dt className="text-muted">Budget</dt>
					<dd className="text-content">
						{budgetSummary(
							campaign.dailyBudgetAmountLocalMicro,
							campaign.totalBudgetAmountLocalMicro,
							campaign.currency,
						)}
					</dd>
					{METRICS.map((metric) => {
						// One call, one decision, one wording — the Spend column's, metric named.
						const state = metricRowState(
							{
								id: campaign.id,
								asked: [campaign.id],
								failed: campaignFiguresFailed !== null,
								answer: campaignFigures,
							},
							metric,
						);
						return (
							<div key={metric} className="contents">
								<dt className="text-muted">{FIGURE_LABELS[metric]}</dt>
								<dd className={state.kind === "figure" ? "text-content" : "text-xs text-muted"}>
									{figureCellText(state, metric, currency)}
								</dd>
							</div>
						);
					})}
					<dt className="text-muted">Paid by</dt>
					<dd className="font-mono text-xs text-content">{campaign.fundingInstrumentId}</dd>
					<dt className="text-muted">Created</dt>
					<dd className="text-content" title={created.utc}>
						{created.local}
					</dd>
					<dt className="text-muted">Updated</dt>
					<dd className="text-content" title={updated.utc}>
						{updated.local}
					</dd>
				</dl>
				<p className="mt-2 text-sm text-muted">{originText(created.local)}</p>
				{campaignFiguresFailed && (
					<InfoBanner variant="warning" title={SPEND_READ_FAILED}>
						{campaignFiguresFailed}
					</InfoBanner>
				)}

				{campaignNotice && (
					<InfoBanner variant={campaignNotice.variant} title={campaignNotice.title}>
						{campaignNotice.body}
					</InfoBanner>
				)}
				{!writable && <p className="mt-2 text-sm text-muted">{CAMPAIGN_READ_ONLY}</p>}
				{writable && edit === null && !confirmingCampaign && (
					<div className="mt-3 flex flex-wrap items-center gap-3">
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={busy}
							onClick={() => {
								setCampaignNotice(null);
								setEditProblem(null);
								setEdit(campaignEdit(campaign));
							}}
						>
							{EDIT}
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={busy}
							onClick={() => {
								setCampaignNotice(null);
								setConfirmingCampaign(true);
							}}
						>
							{DELETE_CAMPAIGN}
						</Button>
					</div>
				)}
				{writable && edit !== null && (
					<form
						className="mt-3 grid max-w-xl gap-3 rounded-lg border border-line p-3"
						onSubmit={(e) => {
							e.preventDefault();
							void saveCampaign();
						}}
					>
						<p className="text-sm text-muted">{CAMPAIGN_EDIT_HELP}</p>
						<Input
							label="Name"
							value={edit.name}
							onChange={(e) => setEdit({ ...edit, name: e.target.value })}
							helperText={CAMPAIGN_NAME_HELP}
						/>
						{campaignDailyOffered(campaign) ? (
							<Input
								label={`Daily budget${campaign.currency ? ` (${campaign.currency})` : ""}`}
								inputMode="decimal"
								value={edit.daily}
								onChange={(e) => setEdit({ ...edit, daily: e.target.value })}
							/>
						) : (
							<p className="text-xs text-muted">{CAMPAIGN_DAILY_ON_AD_GROUPS}</p>
						)}
						<Input
							label={`Total budget${campaign.currency ? ` (${campaign.currency})` : ""}`}
							inputMode="decimal"
							value={edit.total}
							onChange={(e) => setEdit({ ...edit, total: e.target.value })}
						/>
						<p className="text-xs text-muted">{BUDGET_HINT}</p>
						<p className="text-xs text-muted">
							Paid by <span className="font-mono">{campaign.fundingInstrumentId}</span> —{" "}
							{INSTRUMENT_FIXED}
						</p>
						{editProblem && <InfoBanner variant="danger" title={editProblem} />}
						<div className="flex flex-wrap items-center gap-3">
							<Button type="submit" size="sm" loading={busy}>
								{SAVE}
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								disabled={busy}
								onClick={() => setEdit(null)}
							>
								{CANCEL}
							</Button>
						</div>
					</form>
				)}
				{writable && confirmingCampaign && (
					<InfoBanner variant="warning" title={`${DELETE_CAMPAIGN}?`}>
						<p>
							{deleteCampaignConfirm(
								campaign,
								rows.map((r) => r.id),
							)}
						</p>
						{!deletable(campaign.entityStatus) && <p className="mt-2">{PAUSE_FIRST}</p>}
						<div className="mt-3 flex flex-wrap items-center gap-3">
							<Button
								type="button"
								size="sm"
								loading={busy}
								disabled={!deletable(campaign.entityStatus)}
								onClick={() => void removeCampaign()}
							>
								{CONFIRM_DELETE}
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								disabled={busy}
								onClick={() => setConfirmingCampaign(false)}
							>
								{KEEP}
							</Button>
						</div>
					</InfoBanner>
				)}
			</Panel>

			<Panel title={CARD_LINE_ITEMS_PANEL}>
				{lineItems.state === "failed" && (
					<InfoBanner variant="danger" title="X did not answer about this campaign's line items.">
						{lineItems.why}
					</InfoBanner>
				)}
				<Table<LineItem>
					columns={[
						{
							id: "id",
							header: "Line item",
							accessor: (r) => (
								<span className="font-mono text-xs text-content">
									{r.id}
									{r.name !== null && <span className="ml-2 font-sans text-muted">{r.name}</span>}
								</span>
							),
						},
						{
							id: "show",
							header: "",
							accessor: (r) => (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => setShown((open) => (open === r.id ? null : r.id))}
								>
									{shown === r.id ? HIDE_LINE_ITEM : SHOW_LINE_ITEM}
								</Button>
							),
						},
						{
							id: "objective",
							header: "Objective",
							accessor: (r) => (
								<span className="text-xs text-content">{objectiveLabel(r.objective)}</span>
							),
						},
						{
							id: "status",
							header: "Status",
							accessor: (r) => (
								<>
									<Badge variant={r.entityStatus === "ACTIVE" ? "info" : "default"}>
										{r.entityStatus}
									</Badge>
									{creativeHint(r.entityStatus) && (
										<span className="ml-2 text-xs text-muted">{creativeHint(r.entityStatus)}</span>
									)}
								</>
							),
						},
						{
							id: "flight",
							header: "Flight",
							accessor: (r) => {
								const flight = flightLocal(r);
								return (
									<span className="text-xs text-content" title={flight.utc}>
										{flight.local}
									</span>
								);
							},
						},
						{
							id: "shape",
							header: "Placement · product · bid",
							accessor: (r) => (
								<span className="text-xs text-muted">
									{r.placements.join(", ")} · {r.productType} · {bidText(r)}
								</span>
							),
						},
						{
							id: "budget",
							header: "Budget",
							accessor: (r) => (
								<span className="text-xs text-muted">
									{lineItemBudgetText(r, campaign.currency)}
								</span>
							),
						},
						...METRICS.map((metric) => ({
							id: metric,
							header: FIGURE_LABELS[metric],
							accessor: (r: LineItem) => {
								const state = metricRowState(
									{
										id: r.id,
										asked: picked.ids,
										failed: lineItemFiguresFailed !== null,
										answer: lineItemFigures,
									},
									metric,
								);
								return (
									<span
										className={
											state.kind === "figure" ? "text-sm text-content" : "text-xs text-muted"
										}
									>
										{figureCellText(state, metric, currency)}
									</span>
								);
							},
						})),
					]}
					data={rows}
					keyExtractor={(r) => r.id}
					failed={lineItems.state === "failed"}
					loading={lineItems.state === "loading"}
					emptyMessage={noLineItemsText(campaign.servable, campaign.reasonsNotServable)}
				/>
				{lineItems.state === "ready" && rows.length > 0 && (
					<p className="mt-2 text-sm text-muted">
						{shownLabel(rows.length, lineItems.data.moreNotRead)}
					</p>
				)}
				{coveredText(picked.cut) && (
					<p className="mt-1 text-xs text-muted">{coveredText(picked.cut)}</p>
				)}
				{lineItemFiguresFailed && (
					<InfoBanner variant="warning" title={SPEND_READ_FAILED}>
						{lineItemFiguresFailed}
					</InfoBanner>
				)}
				{/*
				 * What a figure on this card is NOT, said once under the line items rather than
				 * in every cell — the Campaigns screen's four notes, verbatim, plus which three
				 * figures these are, from where, and what X's two click metrics are.
				 */}
				<div className="mt-3 space-y-1">
					{figuresRead && <p className="text-xs text-muted">{figuresReadText(figuresRead)}</p>}
					<p className="text-xs text-muted">{FIGURES_NOTE}</p>
					{SPEND_NOTES.map((note) => (
						<p key={note.slice(0, 24)} className="text-xs text-muted">
							{note}
						</p>
					))}
				</div>
				<p className="mt-2 text-sm text-muted">{LINE_ITEMS_HERE}</p>
				<Button type="button" variant="outline" size="sm" onClick={onOpenLineItems}>
					{OPEN_LINE_ITEMS}
				</Button>
			</Panel>

			{/*
			 * 🔴 ONE line item's blocks, the shown one's — not one stack per line item. The block
			 * is the same code it was, with the same guards and handlers; what changed is that
			 * `rows` is filtered to the row whose Show was pressed, so a Delete or a Remove is
			 * always aimed at the line item named in this panel's title.
			 */}
			<Panel
				title={CARD_LINE_ITEM_PANEL}
				aside={
					shownItem ? (
						<span className="font-mono text-xs text-muted">{shownItem.id}</span>
					) : undefined
				}
			>
				{shownItem === null && <p className="text-sm text-muted">{CARD_LINE_ITEM_EMPTY}</p>}

				{countries.state === "failed" && (
					<InfoBanner variant="warning" title={COUNTRIES_NOT_READ.title}>
						{COUNTRIES_NOT_READ.body} {countries.why}
					</InfoBanner>
				)}
				{tweets.state === "failed" && (
					<InfoBanner variant="warning" title={TWEETS_NOT_READ}>
						{tweets.why}
					</InfoBanner>
				)}

				{rows
					.filter((r) => r.id === shown)
					.map((item) => {
						const read = reads[item.id] ?? {
							criteria: { state: "loading" as const },
							promoted: { state: "loading" as const },
						};
						const criteria = read.criteria.state === "ready" ? read.criteria.data : [];
						const promoted = read.promoted.state === "ready" ? read.promoted.data : [];
						const notice = lineItemNotices[item.id];
						const isEditing = editing?.id === item.id;
						return (
							<div key={item.id}>
								<h4 className="text-sm font-semibold text-content">
									Line item
									<span className="ml-2 font-mono text-xs text-muted">{item.id}</span>
									{item.name !== null && (
										<span className="ml-2 text-xs text-muted">{item.name}</span>
									)}
								</h4>
								{notice && (
									<InfoBanner variant={notice.variant} title={notice.title}>
										{notice.body}
									</InfoBanner>
								)}
								{writable && !isEditing && confirming !== item.id && (
									<div className="mt-2 flex flex-wrap items-center gap-3">
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={busy}
											onClick={() => {
												setEditingProblem(null);
												setEditing({ id: item.id, edit: lineItemEdit(item) });
											}}
										>
											{EDIT}
										</Button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={busy}
											onClick={() => setAddingAdTo((open) => (open === item.id ? null : item.id))}
										>
											{ADD_AD}
										</Button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={busy}
											onClick={() => setConfirming(item.id)}
										>
											{DELETE_LINE_ITEM}
										</Button>
									</div>
								)}
								{writable && isEditing && editing && (
									<form
										className="mt-2 grid max-w-xl gap-3 rounded-lg border border-line p-3"
										onSubmit={(e) => {
											e.preventDefault();
											void saveLineItem(item);
										}}
									>
										<p className="text-sm text-muted">{LINE_ITEM_EDIT_HELP}</p>
										<Input
											label="Name"
											value={editing.edit.name}
											onChange={(e) =>
												setEditing({ id: item.id, edit: { ...editing.edit, name: e.target.value } })
											}
											helperText={LINE_ITEM_NAME_HELP}
										/>
										<Input
											type="datetime-local"
											label="Start"
											value={editing.edit.start}
											onChange={(e) =>
												setEditing({
													id: item.id,
													edit: { ...editing.edit, start: e.target.value },
												})
											}
										/>
										<Input
											type="datetime-local"
											label="End"
											value={editing.edit.end}
											onChange={(e) =>
												setEditing({ id: item.id, edit: { ...editing.edit, end: e.target.value } })
											}
										/>
										<p className="text-xs text-muted">{FLIGHT_CAUTION}</p>
										<Input
											label={`Total budget${campaign.currency ? ` (${campaign.currency})` : ""}`}
											inputMode="decimal"
											value={editing.edit.total}
											onChange={(e) =>
												setEditing({
													id: item.id,
													edit: { ...editing.edit, total: e.target.value },
												})
											}
											helperText={LINE_ITEM_TOTAL_CAUTION}
										/>
										{dailyEditable(campaign) ? (
											<Input
												label={`Daily budget${campaign.currency ? ` (${campaign.currency})` : ""}`}
												inputMode="decimal"
												value={editing.edit.daily}
												onChange={(e) =>
													setEditing({
														id: item.id,
														edit: { ...editing.edit, daily: e.target.value },
													})
												}
												helperText={LINE_ITEM_DAILY_HELP}
											/>
										) : (
											<p className="text-xs text-muted">
												{lineItemBudgetText(item, campaign.currency)} — {LINE_ITEM_DAILY_READ_ONLY}
											</p>
										)}
										<p className="text-xs text-muted">
											{objectiveLabel(item.objective)} · {item.productType} ·{" "}
											{item.placements.join(", ")} — {SHAPE_FIXED}
										</p>
										{editingProblem && <InfoBanner variant="danger" title={editingProblem} />}
										<div className="flex flex-wrap items-center gap-3">
											<Button type="submit" size="sm" loading={busy}>
												{SAVE}
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												disabled={busy}
												onClick={() => setEditing(null)}
											>
												{CANCEL}
											</Button>
										</div>
									</form>
								)}
								{writable && confirming === item.id && (
									<InfoBanner variant="warning" title={`${DELETE_LINE_ITEM}?`}>
										<p>
											{deleteLineItemConfirm(
												item,
												promoted.map((p) => p.id),
											)}
										</p>
										{!deletable(item.entityStatus) && <p className="mt-2">{PAUSE_FIRST}</p>}
										<div className="mt-3 flex flex-wrap items-center gap-3">
											<Button
												type="button"
												size="sm"
												loading={busy}
												disabled={!deletable(item.entityStatus)}
												onClick={() => void removeLineItem(item)}
											>
												{CONFIRM_DELETE}
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												disabled={busy}
												onClick={() => setConfirming(null)}
											>
												{KEEP}
											</Button>
										</div>
									</InfoBanner>
								)}

								<h4 className="mt-4 text-sm font-semibold text-content">
									Audience — line item
									<span className="ml-2 font-mono text-xs text-muted">{item.id}</span>
								</h4>
								{read.criteria.state === "failed" && (
									<InfoBanner
										variant="danger"
										title="This line item's targeting could not be read."
									>
										{read.criteria.why}
									</InfoBanner>
								)}
								<Table<TargetingCriterion>
									columns={[
										{
											id: "what",
											header: "Criterion",
											accessor: (c) => (
												<span className="text-xs text-content">{criterionLabel(c)}</span>
											),
										},
										{ id: "name", header: "Country", accessor: nameCell },
										{
											id: "id",
											header: "",
											accessor: (c) => <span className="font-mono text-xs text-muted">{c.id}</span>,
										},
										...(writable
											? [
													{
														id: "remove",
														header: "",
														accessor: (c: TargetingCriterion) =>
															removing?.id === c.id ? (
																<span className="flex flex-wrap items-center gap-2 text-xs text-content">
																	{removePlaceConfirm(
																		countryName(c, list) ?? criterionLabel(c),
																		criteria.length - 1,
																		startGate(criteria.filter((k) => k.id !== c.id)) ===
																			"excludes-only",
																	)}
																	<Button
																		type="button"
																		size="sm"
																		loading={busy}
																		onClick={() => void removePlace(item, c)}
																	>
																		{CONFIRM_DELETE}
																	</Button>
																	<Button
																		type="button"
																		variant="ghost"
																		size="sm"
																		disabled={busy}
																		onClick={() => setRemoving(null)}
																	>
																		{KEEP}
																	</Button>
																</span>
															) : (
																<Button
																	type="button"
																	variant="ghost"
																	size="sm"
																	disabled={busy}
																	onClick={() => setRemoving({ lineItemId: item.id, id: c.id })}
																>
																	{REMOVE_PLACE}
																</Button>
															),
													},
												]
											: []),
									]}
									data={criteria}
									keyExtractor={(c) => c.id}
									failed={read.criteria.state === "failed"}
									loading={read.criteria.state === "loading"}
									emptyMessage={NO_CRITERIA.title}
								/>
								{read.criteria.state === "ready" && criteria.length > 0 && (
									<p className="mt-2 text-sm text-muted">{loadedLabel(criteria.length)}</p>
								)}

								<h4 className="mt-4 text-sm font-semibold text-content">
									Ads — line item
									<span className="ml-2 font-mono text-xs text-muted">{item.id}</span>
								</h4>
								{read.promoted.state === "failed" && (
									<InfoBanner variant="danger" title="X's promoted tweets could not be read.">
										{read.promoted.why}
									</InfoBanner>
								)}
								<Table<PromotedTweet>
									columns={[
										{
											id: "what",
											header: "Promoted tweet",
											accessor: (p) => (
												<span className="text-xs text-content">{promotedLabel(p)}</span>
											),
										},
										{
											id: "id",
											header: "",
											accessor: (p) => <span className="font-mono text-xs text-muted">{p.id}</span>,
										},
										{
											id: "text",
											header: "Tweet",
											accessor: (p) => {
												const state = tweetText(p.tweetId, ask, answer);
												const aside = state.kind === "found" ? tweetAside(state.tweet) : null;
												return (
													<span className="whitespace-normal">
														<span
															className={
																state.kind === "found"
																	? "text-xs text-content"
																	: "text-xs text-muted"
															}
														>
															{tweetTextCell(state)}
														</span>
														{aside && <span className="ml-2 text-xs text-muted">{aside}</span>}
													</span>
												);
											},
										},
										{
											id: "approval",
											header: "X's review",
											accessor: (p) => (
												<span className="whitespace-normal">
													<span className="text-xs text-muted">
														{approvalNote(p.approvalStatus)}
													</span>
													{offersRawAnswer(p.approvalStatus) && (
														<RawAnswer id={p.id} account={account.xAccountId} />
													)}
												</span>
											),
										},
									]}
									data={promoted}
									keyExtractor={(p) => p.id}
									failed={read.promoted.state === "failed"}
									loading={read.promoted.state === "loading"}
									emptyMessage={NO_AD}
								/>
								{writable && addingAdTo === item.id && (
									<AddAd
										// Keyed on the line item, for the reason the targeting panel is: another row is another form.
										key={item.id}
										lineItem={item}
										account={account}
										onAttached={readAll}
									/>
								)}
							</div>
						);
					})}
			</Panel>
		</section>
	);
}
